import React from 'react';
import {
  Checkbox, Box, Typography, MenuItem, Select, ListItemText, Chip, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// New props:
// - isPaid: boolean
// - onRequirePro: () => void  (e.g., loginWithRedirect or open pricing)
// - freeAllowedIds?: string[]  (defaults to ["Software", "Software Median"])
const FilterTableFields = ({
  checked,
  handleToggle,
  items,
  isPaid,
  onRequirePro,
  freeAllowedIds = ['Software', 'Software Median'],
}) => {
  const theme = useTheme();
  const freeSet = React.useMemo(() => new Set(freeAllowedIds), [freeAllowedIds]);
  const effectiveChecked = React.useMemo(
    () => (isPaid ? checked : checked.filter(id => freeSet.has(id))),
    [checked, isPaid, freeSet]
  );
  const proChip = (
    <Chip label="PRO" size="small" variant="outlined"
      sx={{ color: theme.palette.primary.main, fontSize:'9px', ml:1, fontWeight:'bold', textTransform:'uppercase', height:18 }} />
  );


  const renderValue = (selected) => {
    const maxChars = 46;
    const txt = selected.join(', ');
    const display = txt.length > maxChars ? `${txt.slice(0, maxChars)}...` : txt;
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px', pt: '3px' }}>
        {display || 'Filter by Category'}
      </Typography>
    );
  };

  // Intercept clicks on locked checkboxes
  const makeCheckboxHandlers = (locked) => ({
    onClick: (e) => {
      e.stopPropagation();
      if (locked) {
        e.preventDefault();
        onRequirePro?.();
      }
    },
    onChange: (e) => {
      if (locked) {
        e.preventDefault();
        return;
      }
      handleToggle(e);
    },
  });

  return (
    <Box
      size="small"
      sx={{
        height: '30px',
        width: 'auto',
        display: 'flex',
        flexDirection: 'row',
        minWidth: '240px',
        mt: '0px',
        maxWidth: '100%',
        flex: 1,
      }}
    >
      <Select
        size="small"
        multiple
        value={effectiveChecked}
        renderValue={renderValue}
        sx={{
          backgroundColor: theme.tableHeader.highlight,
          width: '308px',
          height: '30px',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }}
        MenuProps={{
          PaperProps: {
            elevation: 4,
            sx: {
              maxHeight: 250,
              backgroundColor: theme.background.transparent,
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              width: 'auto',
              mt: 0.5,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.03)'
              : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
            }}
          >
            Category
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                fontSize: '9px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
            >
              Cap Wt
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                fontSize: '9px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
            >
              Median
            </Typography>
          </Box>
        </Box>

        {/* Items List */}
        {items.map((item) => {
          const lockedCW = !isPaid && !freeSet.has(item.id);
          const lockedEW = !isPaid && !freeSet.has(item.idEW);
          const rowLocked = lockedCW && lockedEW;
          const isCapSelected = effectiveChecked.includes(item.id);
          const isEWSelected = effectiveChecked.includes(item.idEW);
          const isAnySelected = isCapSelected || isEWSelected;

          return (
            <MenuItem
              key={item.id}
              sx={{
                minHeight: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                backgroundColor: isAnySelected
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(145, 166, 205, 0.16)'
                    : 'rgba(63, 100, 168, 0.16)'
                  : 'transparent',
                borderLeft: isAnySelected
                  ? `2px solid ${theme.palette.primary.main}`
                  : '2px solid transparent',
                transition: 'background-color 0.1s ease',
                ...(rowLocked ? { opacity: 0.5 } : {}),
                cursor: rowLocked ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              // If user clicks anywhere on a fully locked row, call CTA.
              onClick={(e) => {
                if (rowLocked) {
                  e.preventDefault();
                  e.stopPropagation();
                  onRequirePro?.();
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '12px',
                        lineHeight: 1.2,
                      }}
                      variant="body2"
                    >
                      {item.label}
                    </Typography>
                    {(lockedCW || lockedEW) && (
                      <Tooltip title="Requires paid plan">{proChip}</Tooltip>
                    )}
                  </Box>
                }
                sx={{ my: 0 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Cap Weight checkbox */}
                <Checkbox
                  value={item.id}
                  checked={isCapSelected}
                  {...makeCheckboxHandlers(lockedCW)}
                  size="small"
                  sx={{
                    p: 0.5,
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                    ...(lockedCW ? {
                      '&.Mui-checked': { color: theme.palette.action.disabled },
                      color: theme.palette.action.disabled,
                    } : {}),
                  }}
                />
                {/* Median checkbox */}
                <Checkbox
                  value={item.idEW}
                  checked={isEWSelected}
                  {...makeCheckboxHandlers(lockedEW)}
                  size="small"
                  sx={{
                    p: 0.5,
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                    ...(lockedEW ? {
                      '&.Mui-checked': { color: theme.palette.action.disabled },
                      color: theme.palette.action.disabled,
                    } : {}),
                  }}
                />
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

export default FilterTableFields;
